import React, { useState } from 'react';
import { Bell, User, Users, Send, CheckCircle, XCircle, MessageSquare, Gift, AlertTriangle, Info, Star, Heart } from 'lucide-react';
import { authToken } from '../globals/Global';
import { sendToAllUsers, sendToUser } from '../globals/Services';

const NotificationCenter = () => {
  const [selectedTab, setSelectedTab] = useState('individual');
  const [userId, setUserId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const categories = [
    { id: 'general', label: 'General', icon: MessageSquare, color: 'bg-blue-500', description: 'General announcements and updates' },
    { id: 'promotion', label: 'Promotion', icon: Gift, color: 'bg-green-500', description: 'Special offers and promotions' },
    { id: 'alert', label: 'Alert', icon: AlertTriangle, color: 'bg-red-500', description: 'Important alerts and warnings' },
    { id: 'info', label: 'Info', icon: Info, color: 'bg-indigo-500', description: 'Informational messages' },
    { id: 'achievement', label: 'Achievement', icon: Star, color: 'bg-yellow-500', description: 'Achievements and milestones' },
    { id: 'social', label: 'Social', icon: Heart, color: 'bg-pink-500', description: 'Social interactions and updates' }
  ];

  const messageTemplates = {
    general: [
      "Welcome to our platform! We're excited to have you here.",
      "Your account has been successfully updated.",
      "Thank you for being a valued member of our community.",
      "New features are now available in your dashboard."
    ],
    promotion: [
      "🎉 Special offer just for you! Get 5% off your next booking.",
      "Limited time deal: Free shipping on all orders this week!",
      "Your exclusive discount code is ready: SAVE10",
      "Festival alert! Don't miss out on these amazing deals."
    ],
    alert: [
      "⚠ Important: Your subscription expires in 3 days.",
      "Security alert: Please verify your account immediately.",
      "Action required: Update your payment information.",
      "System maintenance scheduled for tonight at 2 AM EST."
    ],
    info: [
      "ℹ Your monthly report is now available.",
      "New tutorial: Learn how to maximize your experience.",
      "Did you know? You can customize your notification preferences.",
      "Quick tip: Use our mobile app for better convenience."
    ],
    achievement: [
      "🏆 Congratulations! You've chanted 108 times.",
      "⭐ Achievement unlocked: You're now a KRANTI pack member!",
      "🎯 Goal completed! You've finished your first pooja.",
      "🎖 Badge earned: Active community member for 30 days."
    ],
    social: [
      "💬 You have new messages from your jajmaan.",
      "👥 Someone wants to connect with you for booking!",
      "❤ Your profile reached 10 jajmaans.",
      "🎂 It's your friend's birthday today - send a wish!"
    ]
  };

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory) || categories[0];

  const applyTemplate = (template) => {
    setMessage(template);
  };

  const sendNotification = async () => {
    if (!message.trim()) {
      showNotification('Please enter a notification message', 'error');
      return;
    }

    if (selectedTab === 'individual' && !userId.trim()) {
      showNotification('Please enter a user ID', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const token = authToken || localStorage.getItem('sessionToken');
      
      // Determine API endpoint and payload based on selected tab
      const isIndividual = selectedTab === 'individual';
      const apiUrl = isIndividual 
        ? sendToUser
        : sendToAllUsers;
      
      const requestBody = isIndividual
        ? {
            userId: parseInt(userId, 10),
            body: message.trim()
          }
        : {
            body: message.trim()
          };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Failed to send notification';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else {
            const textError = await response.text();
            errorMessage = textError || `Failed to send notification (${response.status})`;
          }
        } catch (e) {
          errorMessage = `Failed to send notification (${response.status})`;
        }
        throw new Error(errorMessage);
      }

      // Handle response - check if it's JSON or plain text
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          await response.json();
        } else {
          // Handle plain text response
          await response.text();
        }
      } catch (parseError) {
        // If parsing fails, it's okay - the request was still successful
        console.log('Response parsing failed but request succeeded:', parseError);
      }
      
      const recipient = selectedTab === 'individual' ? `user ${userId}` : 'all users';
      showNotification(`Notification sent successfully to ${recipient}!`, 'success');
      
      // Reset form
      if (selectedTab === 'individual') {
        setUserId('');
      }
      setMessage('');
      
    } catch (error) {
      console.error('Notification send error:', error);
      showNotification(error.message || 'Failed to send notification. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex items-center gap-5 mb-2">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Bell className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Notification Center</h1>
              <p className="text-gray-600">Send targeted notifications to users</p>
            </div>
          </div>
        </div>

        {/* Notification Toast */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 max-w-md ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <XCircle className="w-5 h-5 flex-shrink-0" />}
            <span className="text-sm">{notification.message}</span>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Notification Type Tabs */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setSelectedTab('individual')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedTab === 'individual'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <User className="w-4 h-4" />
                  Individual User
                </button>
                <button
                  onClick={() => setSelectedTab('broadcast')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedTab === 'broadcast'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Broadcast to All
                </button>
              </div>

              {/* User ID Input (for individual notifications) */}
              {selectedTab === 'individual' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User ID *
                  </label>
                  <input
                    type="number"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Enter user ID (e.g., 28)"
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  />
                </div>
              )}

              {/* Category Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Notification Category
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                          selectedCategory === category.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${category.color} flex items-center justify-center mb-2 mx-auto`}>
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">{category.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message Composer */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Message *
                </label>
                <div className="relative">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your notification message..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                  />
                  <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                    {message.length}/500
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <button
                onClick={sendNotification}
                disabled={isLoading}
                className={`w-full py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                  isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 transform hover:scale-[1.02]'
                } text-white shadow-lg`}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send {selectedTab === 'individual' ? 'to User' : 'to All Users'}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Templates Sidebar */}
          <div className="space-y-6">
            {/* Category Info */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg ${selectedCategoryData.color} flex items-center justify-center`}>
                  <selectedCategoryData.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedCategoryData.label}</h3>
                  <p className="text-sm text-gray-600">{selectedCategoryData.description}</p>
                </div>
              </div>
            </div>

            {/* Message Templates */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Message Templates</h3>
              <div className="space-y-3">
                {messageTemplates[selectedCategory].map((template, index) => (
                  <button
                    key={index}
                    onClick={() => applyTemplate(template)}
                    className="w-full p-3 text-left text-sm border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    {template}
                  </button>
                ))}
              </div>
            </div>

            {/* API Info Panel */}
            {/* <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">API Information</h3>
              <div className="text-xs space-y-2">
                <div className="bg-gray-50 p-2 rounded">
                  <div className="font-medium text-gray-700">Individual:</div>
                  <div className="text-gray-600">POST /api/notifications/sendToUser</div>
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <div className="font-medium text-gray-700">Broadcast:</div>
                  <div className="text-gray-600">POST /api/notifications/sendToAllUsers</div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;